/* APPETITT — maskoten, i tre stadier.
 *
 * Animasjonen er en cables.gl-eksport fra /opt/phoniq-mascot, kopiert inn
 * under public/mascot/ fordi bygget kjører i en container som bare ser dette
 * repoet. En ny eksport der må kopieres hit på nytt.
 *
 * STADIENE ER EN FORHÅNDSVISNING
 *   Sulten, nja og ikke interessert velges for hånd her. Ingenting leser
 *   appetitten fra data ennå; knappene er for å se hvordan hvert stadium ser
 *   ut og oppfører seg rundt pellets, før det bestemmes hva som skal styre dem.
 *
 * HVORFOR SIDEN SJEKKER KJØPET SELV
 *   De andre produktsidene leser et endepunkt som svarer 403 for en lokalitet
 *   som ikke har kjøpt produktet, og lener seg på det. Denne leser ingen data,
 *   så uten sjekken ville hvem som helst med lenken fått produktet.
 *
 * ÉN MASKOT PER SIDE
 *   Cables-kjøretiden holder patchen i den globale CABLES.patch, og mount() er
 *   asynkron. StrictMode monterer, avmonterer og monterer igjen, så uten køen
 *   under ville den andre monteringen kastet «already mounted» før den første
 *   var ryddet bort.
 */

import { useEffect, useRef, useState } from 'react'

import type { SiteDetail } from '../api'
import { ErrorBox, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { useT } from '../lang'
import { useApi } from '../useApi'

const FEATURE = 'analysis.appetitt'
const BASE = '/mascot/'

type Stage = 'hungry' | 'meh' | 'uninterested'
const STAGES: { id: Stage; label: string }[] = [
  { id: 'hungry', label: 'Hungry' },
  { id: 'meh', label: 'Meh' },
  { id: 'uninterested', label: 'Not interested' },
]

interface Mascot {
  destroy(): void
  setStage(s: Stage): void
  feed(n?: number): void
}
declare global {
  interface Window {
    PhoniqMascot?: {
      mount(el: HTMLElement, o: {
        basePath: string; darkmode?: number; stage?: Stage; autoFeed?: number
      }): Promise<Mascot>
    }
  }
}

let script: Promise<void> | null = null
function loadMascot(): Promise<void> {
  if (window.PhoniqMascot) return Promise.resolve()
  script ??= new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = `${BASE}mascot.js`
    s.onload = () => resolve()
    s.onerror = () => { script = null; reject(new Error('Could not load the animation')) }
    document.head.appendChild(s)
  })
  return script
}

let queue: Promise<unknown> = Promise.resolve()
function serial(f: () => Promise<void>): Promise<void> {
  const p = queue.then(f)
  queue = p.catch(() => {})
  return p
}

function Animasjon({ stage, feeds }: { stage: Stage; feeds: number }) {
  const el = useRef<HTMLDivElement>(null)
  const mascot = useRef<Mascot | null>(null)
  const [error, setError] = useState<Error | null>(null)
  // Read at mount, so the first stage is right without a flash of the default.
  const first = useRef(stage)

  useEffect(() => {
    let cancelled = false
    serial(async () => {
      await loadMascot()
      if (cancelled || !el.current) return
      mascot.current = await window.PhoniqMascot!.mount(el.current, {
        basePath: BASE, darkmode: 0, stage: first.current, autoFeed: 2200,
      })
    }).catch((e: Error) => { if (!cancelled) setError(e) })
    return () => {
      cancelled = true
      serial(async () => { mascot.current?.destroy(); mascot.current = null })
    }
  }, [])

  useEffect(() => { mascot.current?.setStage(stage) }, [stage])
  useEffect(() => { if (feeds) mascot.current?.feed(3) }, [feeds])

  if (error) return <ErrorBox error={error} />
  return (
    <div ref={el} className="card"
      style={{ height: 'min(66vh, 720px)', padding: 0, overflow: 'hidden' }} />
  )
}

export function SiteAppetitt({ id }: { id: string }) {
  const t = useT()
  const sted = useApi<SiteDetail>(`/v1/sites/${encodeURIComponent(id)}`)
  const [stage, setStage] = useState<Stage>('hungry')
  const [feeds, setFeeds] = useState(0)

  if (sted.error) return <ErrorBox error={sted.error} />
  if (sted.cold) return <Rows n={3} />

  if (!sted.data?.features?.includes(FEATURE)) {
    return (
      <>
        <PageHead title={t('Appetite')} lead={sted.data?.name} tight />
        <p className="empty">{t('This farm does not have Appetite.')}</p>
      </>
    )
  }

  return (
    <>
      <PageHead title={t('Appetite')} lead={sted.data.name} tight />
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {STAGES.map((s) => (
          <button key={s.id} aria-pressed={stage === s.id}
            className={`btn sm${stage === s.id ? ' primary' : ''}`}
            onClick={() => setStage(s.id)}>
            {t(s.label)}
          </button>
        ))}
        <button className="btn sm ghost" style={{ marginLeft: 'auto' }}
          onClick={() => setFeeds((n) => n + 1)}>
          {t('Drop feed')}
        </button>
      </div>
      <Animasjon stage={stage} feeds={feeds} />
    </>
  )
}
